import React, {PureComponent} from 'react';
import styled from 'styled-components';
import {Switch, Route, withRouter} from 'react-router-dom';
import {NotificationManager} from 'react-notifications';

import * as api from '../../lib/api';
import {withUser} from '../../lib/user';
import TimeAgo from '../../components/TimeAgo';
import Content from '../../components/Content';
import Loading from '../../components/Loading';
import SideModal from '../../components/SideModal';
import AuditLog from '../../components/AuditLog';
import Answer from '../../components/Answer';
import VirtualizedGrid from '../../components/VirtualizedGrid';
import Icon from '../../components/Icon';
import Tabs from '../../components/Tabs';
import Form from '../../components/Form';
import Button from '../../components/Button';
import TextArea from '../../components/TextArea';
import CanReviewTemplateData from '../../security/rules/CanReviewTemplateData';
import CanEditUser from '../../security/rules/CanEditUser';

import AnswersTab from './AnswersTab';
import {calculateAlerts} from './shared';
import Role from "../../lib/Role";

class ViewDataTab extends PureComponent {
  state = {
    loading: true,
    reviewComment: '',
    submittingReview: false
  };

  async componentDidMount() {
    const {template, sheets, rows} = this.props;
    const worker = new Worker('/workers/view-data-tab-worker.js');

    // Invoke the worker.
    worker.onmessage = e => this.setState(e.data);
    worker.postMessage([template, sheets, rows]);
  }

  handleUpdateAnswers = async (sheet, row, question, answers) => {
    const {onAnswersChange} = this.props;
    let {columns, data, rows, rowsById, sheetsById} = this.state;

    // Need to update the copy of our own state as if it was built by the web worker.
    // Update the data in the grid. First, figure out what column the data is stored, then update the appropriate row.
    const columnIndex = columns.map(column => column.key).indexOf(question.id);

    if (columnIndex >= 0) {
      data = data.map(gridRow => {
        const cell = gridRow[columnIndex];

        if (cell.rowId === row.id || (question.context && cell.sheetId === sheet.id)) {
          const copy = [...gridRow];
          copy[columnIndex] = {...copy[columnIndex], answers};

          return copy;
        }

        return gridRow;
      });
    }

    if (question.context) {
      // If the question was a context question, we need to update the contextAnswers property on the correct sheet
      // object.
      sheetsById = {
        ...sheetsById,
        [sheet.id]: {
          ...sheetsById[sheet.id],
          contextAnswers: sheetsById[sheet.id].contextAnswers
            .filter(answer => answer.questionId !== question.id)
            .concat(answers)
        }
      };
    }
    else {
      // If the question was not a context question, then we need to update the rows. Iterate over the rows and update
      // the answers, then rebuild the rowsById map.
      rows = rows.map(prevRow => {
        if (prevRow.id === row.id || (question.context && prevRow.sheetId === sheet.id)) {
          return {
            ...prevRow,
            answers: prevRow.answers
              .filter(answer => answer.questionId !== question.id)
              .concat(answers)
          };
        }

        return prevRow;
      });

      rowsById = rows.reduce((map, row) => {
        map[row.id] = row;
        return map;
      }, {});
    }

    // Commit the changes to the component state.
    this.setState({data, rows, rowsById, sheetsById});

    // Notify the parent that the answers have changed.
    onAnswersChange(sheet, row, question, answers);
  }

  handleCellClick = (cell, rowIndex) => {
    const {user, history, match: {params: {sheetIds, templateId}}} = this.props;
    const {rows} = this.state;
    const row = rows[rowIndex];

    if (cell.type === 'SHEET_LINK') {
      history.push(`/sheet/${cell.sheet.template.id}/${cell.sheet.id}`);
    }
    else if (cell.type === 'USER_LINK' && CanEditUser(user, cell.user)) {
      history.push(`/users/${cell.user.id}`);
    }
    else {
      history.push(`/sheet/${templateId}/${sheetIds}/data/${row.id}`);
    }
  }

  handleCloseRowDetails = () => {
    const {history, match: {params: {sheetIds, templateId}}} = this.props;
    history.push(`/sheet/${templateId}/${sheetIds}/data`);
  }

  handleReviewRow = async (row, state) => {
    const {credentials, onRowChange} = this.props;
    const {sheetsById, reviewComment} = this.state;
    let response;

    try {
      this.setState({submittingReview: true});
      response = await api.reviewSheetRow(row.sheetId, row.id, state, reviewComment, credentials);
    }
    catch (e) {
      this.setState({submittingReview: false});
      NotificationManager.error('There was an error while submitting your review. Please try again.');
      return;
    }

    const nextRow = {...row, ...response};

    // Update the view data tab state to keep it consistent.
    this.setState(prevState => {
      const reviewStatusIndex = prevState.data[0].map(cell => cell.type).indexOf('REVIEW_STATUS');

      return {
        submittingReview: false,
        reviewComment: '',
        rows: prevState.rows.map(row => row.id === nextRow.id ? nextRow : row),
        rowsById: {
          ...prevState.rowsById,
          [nextRow.id]: nextRow
        },
        data: prevState.data.map(gridRow => {
          if (gridRow[reviewStatusIndex].row.id === nextRow.id) {
            // Found the data row that contains the data for this sheet row.
            const copy = [...gridRow];
            copy[reviewStatusIndex] = {...copy[reviewStatusIndex], row: nextRow};
            return copy;
          }

          return gridRow;
        })
      };
    });

    // Notify the parent that the row has changed.
    onRowChange(sheetsById[row.sheetId], response);
  }

  handleDeleteRow = async row => {
    const {onRowDeleted, credentials} = this.props;
    const {sheetsById} = this.state;

    // eslint-disable-next-line
    if (!confirm('Are you sure you want to permanently delete this row? This action cannot be undone!')) {
      return;
    }

    try {
      await api.deleteSheetRow(row.sheetId, row.id, credentials);
      NotificationManager.success('This row was deleted successfully');
      this.handleCloseRowDetails();

      // Update the view data tab state to keep it consistent.
      this.setState(prevState => {
        return {
          rows: prevState.rows.filter(r => r.id !== row.id),
          rowsById: Object.keys(prevState.rowsById).filter(key => key !== row.id).reduce((rowsById, rowId) => {
            rowsById[rowId] = prevState.rowsById[rowId];
            return rowsById;
          }, {}),
          data: prevState.data.filter(dataRow => {
            const anyAnswerColumn = dataRow.filter(cell => cell.type === 'ANSWER')[0];
            console.log(row, anyAnswerColumn);
            return anyAnswerColumn && anyAnswerColumn.rowId !== row.id;
          })
        };
      });

      // Notify the parent component that the row was deleted.
      onRowDeleted(sheetsById[row.sheetId], row);
    }
    catch (e) {
      NotificationManager.error('An error occurred while deleting this row. Please try again.');
    }
  }

  renderRowFooter = row => {
    const {user, template} = this.props;
    const {reviewComment, submittingReview} = this.state;

    return (
      <SheetRowFooter>
        <Form>
          {template.reviewers.length > 0 && CanReviewTemplateData(user, template) && row.reviewState === 'PENDING' && (
            <ReviewCommentContainer>
              <ReviewCommentTextArea
                disabled={submittingReview}
                placeholder="Write a review comment (optional)"
                defaultValue={reviewComment}
                onBlur={e => this.setState({reviewComment: e.target.value})}
              />
            </ReviewCommentContainer>
          )}
          <SheetRowFooterInfo>
            <ReviewStateContainer>
              {template.reviewers.length > 0 && (
                <>
                  {(!CanReviewTemplateData(user, template) || row.reviewState !== 'PENDING') ? (
                    <>
                      <ReviewState inverse state={row.reviewState}>{row.reviewState}</ReviewState>
                      {row.reviewer && <>&nbsp;by&nbsp;<strong>{row.reviewer.userName}</strong>&nbsp;<TimeAgo date={row.reviewDate} /></>}
                    </>
                  ) : (
                    <>
                      <Button
                        secondary
                        disabled={submittingReview}
                        type="button" 
                        onClick={() => this.handleReviewRow(row, 'APPROVED')}>
                        <Icon name="check" /> Approve
                      </Button>
                      <Button 
                        secondary
                        disabled={submittingReview}
                        type="button"
                        onClick={() => this.handleReviewRow(row, 'REJECTED')}>
                        <Icon name="slash" /> Reject
                      </Button>
                    </>
                  )}
                </>
              )}
            </ReviewStateContainer>
            <SheetRowOptions>
              {user.role === 'ADMIN' && (
                <Button destructive onClick={() => this.handleDeleteRow(row)}><Icon name="trash-2" /> Delete Row</Button>
              )}
            </SheetRowOptions>
          </SheetRowFooterInfo>
        </Form>
      </SheetRowFooter>
    );
  }

  renderRowModal = ({location, match: {params: {templateId, sheetIds, id}}}) => {
    const {user} = this.props;
    let row, title, content;
    const isHistoryTab = location.pathname.indexOf('/history') >= 0;

    if (id) {
      const {rowsById} = this.state;
      row = rowsById[id];

      if (!row) {
        return null;
      }

      title = new Date(row.dates.lastModified).toLocaleString();
      content = (
        <Switch>
          <Route path="/sheet/:templateId/:sheetIds/data/:id/history" component={this.renderHistoryTab} />
          <Route path="/sheet/:templateId/:sheetIds/data/:id" component={this.renderAnswersTab} />
        </Switch>
      );
    }

    return (
      <SideModal
        large
        open={!!id}
        title={title}
        footer={!!id && this.renderRowFooter(row)}
        onClose={this.handleCloseRowDetails}
        tabs={user.role === Role.ADMIN && (
          <Tabs>
            <Tabs.Tab active={!isHistoryTab} to={`/sheet/${templateId}/${sheetIds}/data/${id}`}>Answers</Tabs.Tab>
            <Tabs.Tab active={isHistoryTab} to={`/sheet/${templateId}/${sheetIds}/data/${id}/history`}>History</Tabs.Tab>
          </Tabs>
        )}>
        {content}
      </SideModal>
    );
  }

  renderAnswersTab = ({match: {params: {id}}}) => {
    const {template, catalog} = this.props;
    const {sheetsById, rowsById} = this.state;
    const row = rowsById[id];
    const sheet = sheetsById[row.sheetId];

    return (
      <AnswersTab
        template={template}
        catalog={catalog}
        row={row}
        sheet={sheet}
        onAnswersChange={this.handleUpdateAnswers}
      />
    );
  }

  renderHistoryTab = ({match: {params: {id}}}) => {
    const {template, catalog} = this.props;
    const {rowsById} = this.state;
    const row = rowsById[id];

    return (
      <SideModal.Content>
        <AuditLog
          tags={[`sheetRow.${row.id}`]}
          formatValue={(log, value) => {
            switch (log.action) {
              case 'review_state_changed':
                return null;

              default:
                const question = getQuestionFromLog(log, template);
                return <Answer question={question} answers={[{value}]} catalog={catalog} />;
            }
          }}>
          {log => {
            switch (log.action) {
              case 'review_state_changed':
                return <span>Changed the status from <ReviewState state={log.previous[0]}>{log.previous[0]}</ReviewState> to <ReviewState state={log.next[0]}>{log.next[0]}</ReviewState></span>;

              default:
                const question = getQuestionFromLog(log, template);
                return <span>changed the answer for <strong>{question.text}</strong></span>;
            }
          }}
        </AuditLog>
      </SideModal.Content>
    );
  }

  renderCell = (cell, rowIndex, columnIndex, column, measure) => {
    const {template, catalog, user} = this.props;

    switch (cell.type) {
      case 'ALERTS':
        if (calculateAlerts(template, cell.row).length < 1) {
          return null;
        }

        return <Icon color="#FFB300" size="1.5em" name="alert-triangle" />;

      case 'REVIEW_STATUS':
        return <ReviewState fullWidth state={cell.row.reviewState}>{cell.row.reviewState}</ReviewState>;

      case 'DATE':
        return new Date(cell.value).toLocaleString();

      case 'SHEET_LINK':
        return <LinkLookAlike>{cell.sheet.name || template.name}</LinkLookAlike>;

      case 'USER_LINK':
        return CanEditUser(user, cell.user) ? <LinkLookAlike>{cell.user.userName}</LinkLookAlike> : cell.user.userName;

      case 'ANSWER':
        if (!cell.answers || cell.answers.length < 1) {
          return null;
        }

        return (
          <AnswerCell question={cell.question}>
            <Answer question={cell.question} answers={cell.answers} catalog={catalog} onImageLoaded={measure} />
          </AnswerCell>
        );

      case 'VIEW_DETAILS':
        return <LinkLookAlike>View all answers...</LinkLookAlike>;

      default:
        throw new Error(`Unhandled cell type: ${cell.type}`);
    }
  }

  render() {
    const {loading, columns, data} = this.state;

    if (loading) {
      return <Loading message="Loading responses..." />;
    }

    return (
      <Container>
        <Grid
          columns={columns}
          data={data}
          children={this.renderCell}
          onCellClick={this.handleCellClick}
        />
        <Route path="/sheet/:templateId/:sheetIds/data/:id?" component={this.renderRowModal} />
      </Container>
    );
  }
}

function getQuestionFromLog(log, template) {
  return template.questions.filter(question => question.id === log.objectKey)[0];
}

const Container = styled(Content)`
  margin-top: 1.875rem;
  height: calc(100vh - 14rem);
  min-height: 30rem;

  @media (max-width: 50rem) {
    margin-top: 1.25rem;
  }
`;

const Grid = styled(VirtualizedGrid)`
  height: 100%;
`;

const AnswerCell = styled.div`
  width: 100%;

  ${props => {
    if (props.question.type === 'NUMERIC') {
      return `text-align: right;`;
    }
  }}
`;

const LinkLookAlike = styled.span`
  color: #0049ee;
`;

const ReviewState = styled.div`
  display: inline-block;
  padding: 1px 1rem;
  text-align: center;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  background: ${props => {
    if (props.inverse) {
      switch (props.state) {
        case 'REJECTED': return '#a52824';
        case 'APPROVED': return '#3e8f58';
        case 'PENDING':
        default: return '#666b7e';
      }
    }

    switch (props.state) {
      case 'REJECTED': return '#fbedee';
      case 'APPROVED': return '#dff1e5';
      case 'PENDING':
      default: return '#e8edfa';
    }
  }};
  color: ${props => {
    if (props.inverse) {
      return '#ffffff';
    }

    switch (props.state) {
      case 'REJECTED': return '#a52824';
      case 'APPROVED': return '#006e21';
      case 'PENDING':
      default: return '#666b7e';
    }
  }};

  ${props => {
    if (props.fullWidth) {
      return `width: 100%;`;
    }
  }}
`;

const SheetRowFooter = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 0.0625rem solid #e4e4e4;
  padding: 1.25rem;

  ${Button} {
    margin-right: 0.5rem;
  }
`;

const ReviewCommentContainer = styled.div`
  margin-bottom: 0.5rem;
`;

const SheetRowFooterInfo = styled.div`
  display: flex;
`;

const ReviewStateContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
`;

const SheetRowOptions = styled.div`
`;

const ReviewCommentTextArea = styled(TextArea)`
  display: block;
`;

export default withRouter(withUser(ViewDataTab));
